import { useEffect, useState } from "react";
import Logo from "@/components/feature/Logo";
import { navGroups } from "@/mocks/home";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-6">
        <a
          href="#top"
          className="flex items-center gap-2.5 font-heading text-lg font-black tracking-wide text-foreground-950 whitespace-nowrap md:text-2xl"
        >
          <Logo
            className="h-[21px] md:h-[27px]"
            src="https://static.readdy.ai/image/913058b6efbc183fd3b03b474476e73a/32e004783178fd263c556725d0874e6a.png"
          />
          <span className="relative left-[5px] top-[3px] text-[18.9px] md:left-0 md:top-[5px] md:text-[25.2px]">株式会社ナンバ</span>
        </a>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            className="hidden items-center gap-1.5 rounded-md bg-secondary-500 px-4 py-2 font-label text-sm font-bold text-background-50 transition-colors hover:bg-secondary-600 whitespace-nowrap md:flex"
          >
            <i className="ri-video-upload-line text-lg" />
            ファイルを送る
          </a>

          <a
            href="#contact"
            className="hidden items-center gap-1.5 rounded-md px-4 py-2 font-label text-sm font-bold text-primary-500 transition-colors hover:bg-primary-100/70 whitespace-nowrap md:flex"
          >
            <i className="ri-heart-pulse-line text-lg" />
            お問い合わせ
          </a>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="メニュー"
            className="relative top-[4px] flex items-center gap-2 rounded-md px-3 py-2 font-label text-sm font-bold tracking-widest text-foreground-900 transition-colors hover:bg-background-100 whitespace-nowrap md:top-0"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="absolute inset-x-0 top-full max-h-[calc(100vh-4rem)] overflow-y-auto border-b border-background-200 bg-background-50/95 backdrop-blur-md md:max-h-[calc(100vh-5rem)]">
          <nav className="mx-auto w-full max-w-6xl px-4 py-5 md:px-6 md:py-8">
            {/* モバイル用クイック操作 */}
            <div className="mb-5 grid grid-cols-2 gap-2 md:hidden">
              <a
                href="#contact"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-1.5 rounded-md bg-secondary-500 px-4 py-3 font-label text-sm font-bold text-background-50 transition-colors hover:bg-secondary-600"
              >
                <i className="ri-video-upload-line text-lg" />
                ファイルを送る
              </a>
              <a
                href="#contact"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-1.5 rounded-md border border-primary-200 px-4 py-3 font-label text-sm font-bold text-primary-600 transition-colors hover:bg-primary-100/70"
              >
                <i className="ri-mail-send-line text-lg" />
                お問い合わせ
              </a>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-[1.55fr_1fr] md:gap-10">
              {navGroups.map((group) => (
                <div key={group.title} className="min-w-0">
                  <p className="mb-3 flex items-center gap-2 font-label text-xs font-bold tracking-[0.2em] text-foreground-400">
                    <span className="h-px w-4 bg-accent-400" />
                    {group.title}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {group.items.map((item) => {
                      const isExternal = /^https?:\/\//.test(item.href);
                      return (
                        <a
                          key={item.label}
                          href={item.href}
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noopener noreferrer" : undefined}
                          onClick={() => setMenuOpen(false)}
                          className="group flex items-start gap-3 rounded-lg border border-transparent px-3 py-3 transition-colors hover:border-background-200 hover:bg-background-100"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100/70 text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                            <i className={`${item.icon} text-xl`} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5 font-label text-sm font-bold text-foreground-900 transition-colors group-hover:text-primary-600">
                              <span className="truncate">{item.label}</span>
                              <i
                                className={`${
                                  isExternal ? "ri-arrow-right-up-line" : "ri-arrow-right-s-line"
                                } text-sm text-foreground-400 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500`}
                              />
                            </span>
                            <span className="mt-0.5 block font-body text-xs leading-relaxed text-foreground-500">
                              {item.description}
                            </span>
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}